import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";
import { getToken } from "@/utils/auth";
import { getInfo } from "@/api/login";
import { isAdminDomain, isPortalDomain } from "@/utils/siteMode";
import { openPortalLogin } from "@/utils/portalLogin";

function isPublicRoute(route) {
  return (
    route &&
    route.matched &&
    route.matched.length &&
    !route.matched.some(function (m) {
      return m.meta && m.meta.needAuth;
    })
  );
}

function handlePortalNeedAuth(to, from, next) {
  openPortalLogin(to.fullPath || to.path || "/");
  if (isPublicRoute(from) && from.path && from.path !== to.path) {
    next(false);
  } else if (to.path !== "/index") {
    next({ path: "/index", replace: true });
  } else {
    next(false);
  }
}

Vue.use(VueRouter);

/** 避免重复点击同一菜单项时控制台 NavigationDuplicated 未捕获异常 */
const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch((err) => err);
};
const originalReplace = VueRouter.prototype.replace;
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject);
  return originalReplace.call(this, location).catch((err) => err);
};

const routes = [
  { path: "/login", redirect: "/index", meta: { public: true } },
  {
    path: "/admin/login",
    name: "AdminLogin",
    component: () => import("@/views/login/index.vue"),
    meta: { public: true, loginType: "admin" },
  },
  { path: "/portal/login", redirect: "/index", meta: { public: true } },
  { path: "/situation", redirect: "/admin/situation" },
  {
    path: "/admin",
    component: () => import("@/layout/AdminLayout.vue"),
    redirect: "/admin/index",
    meta: { needAuth: true, admin: true },
    children: [
      { path: "index", name: "AdminHome", component: () => import("@/views/admin/Dashboard.vue"), meta: { title: "后台首页" } },
      { path: "situation", name: "AdminSituation", component: () => import("@/views/admin/AdminSituation.vue"), meta: { title: "数据大屏" } },
      { path: "system/dept", name: "Dept", component: () => import("@/views/system/dept/index.vue"), meta: { title: "班级管理" } },
      { path: "system/notice", name: "Notice", component: () => import("@/views/system/notice/index.vue"), meta: { title: "通知公告" } },
      { path: "school/school", name: "SchoolManage", component: () => import("@/views/admin/SchoolManage.vue"), meta: { title: "学院管理" } },
      { path: "school/lesson-quota", name: "SuperLessonManage", component: () => import("@/views/admin/SuperLessonManage.vue"), meta: { title: "课时划拨与消耗" } },
      { path: "school/lesson-manage", name: "CollegeLessonManage", component: () => import("@/views/admin/CollegeLessonManage.vue"), meta: { title: "课时池与划拨" } },
      { path: "student/student", name: "Student", component: () => import("@/views/student/student/index.vue"), meta: { title: "学生管理" } },
      { path: "teacher/teacher", name: "Teacher", component: () => import("@/views/teacher/teacher/index.vue"), meta: { title: "教师管理" } },
      { path: "user/account", name: "AccountManage", component: () => import("@/views/admin/AccountManage.vue"), meta: { title: "全站账号" } },
      { path: "course/course", name: "Course", component: () => import("@/views/course/course/index.vue"), meta: { title: "基础课程" } },
      { path: "course/course-edit", name: "CourseEdit", component: () => import("@/views/course/course/CourseEdit.vue"), meta: { title: "课程编辑" } },
      { path: "course/course-chapters", name: "CourseChapters", component: () => import("@/views/course/course/CourseChapters.vue"), meta: { title: "章节管理" } },
      { path: "course/course_type", name: "CourseType", component: () => import("@/views/admin/CourseTypeManage.vue"), meta: { title: "课程分类" } },
      {
        path: "experiment/direction",
        name: "ExpDirection",
        component: () => import("@/views/admin/ExperimentDirectionManage.vue"),
        meta: { title: "技术方向" },
      },
      { path: "profession/profession", name: "ProfessionManage", component: () => import("@/views/profession/profession/index.vue"), meta: { title: "职业体系" } },
      {
        path: "learn-path/manage",
        name: "LearnPathManage",
        component: () => import("@/views/profession/profession/index.vue"),
        meta: { title: "学习路径", professionTab: "2" },
      },
      { path: "course/discussion-audit", name: "CourseDiscussionAudit", component: () => import("@/views/admin/CourseDiscussionAudit.vue"), meta: { title: "问答审核" } },
      { path: "scene/imageClassify", name: "SceneClassify", component: () => import("@/views/admin/SceneClassifyManage.vue"), meta: { title: "场景镜像分类" } },
      { path: "scene/scheme", name: "SceneScheme", component: () => import("@/views/admin/SceneSchemeManage.vue"), meta: { title: "场景方案" } },
      { path: "docker/image", name: "DockerImage", component: () => import("@/views/admin/DockerImageManage.vue"), meta: { title: "Docker 镜像" } },
      { path: "docker/container", name: "DockerContainer", component: () => import("@/views/admin/DockerContainerManage.vue"), meta: { title: "运行容器" } },
      { path: "operation/userScene", name: "UserScene", component: () => import("@/views/admin/UserSceneManage.vue"), meta: { title: "用户场景" } },
      { path: "train/toolkit", name: "ToolkitManage", component: () => import("@/views/admin/ToolkitManage.vue"), meta: { title: "工具库管理" } },
      { path: "train/bank-manage", name: "QuizSetManageEntry", component: () => import("@/views/admin/QuizSetManage.vue"), meta: { title: "题库管理", defaultTab: "bank" } },
      { path: "train/bank-quiz", name: "QuizSetManage", component: () => import("@/views/admin/QuizSetManage.vue"), meta: { title: "试卷管理", defaultTab: "set" } },
      { path: "train/bank-quiz-ai", name: "AiExamGenerate", component: () => import("@/views/admin/AiExamGenerate.vue"), meta: { title: "AI 出卷" } },
      { path: "train/bank-quiz/:setId", name: "QuizQuestionManage", component: () => import("@/views/admin/QuizQuestionManage.vue"), meta: { title: "题目管理" } },
      { path: "train/cloudstudio", name: "CloudWorkspaceManage", component: () => import("@/views/admin/CloudWorkspaceManage.vue"), meta: { title: "工作空间" } },
      { path: "train/apps", name: "CloudAppManage", component: () => import("@/views/admin/CloudAppManage.vue"), meta: { title: "应用管理" } },
      { path: "university/university", name: "UniversityManage", component: () => import("@/views/admin/UniversityManage.vue"), meta: { title: "高校专区" } },
      { path: "coze/knowledge", name: "CozeKnowledgeManage", component: () => import("@/views/admin/CozeKnowledgeManage.vue"), meta: { title: "平台知识库" } },
      { path: "coze/agent", name: "CozeAgentManage", component: () => import("@/views/admin/CozeAgentManage.vue"), meta: { title: "平台智能体" } },
      { path: "coze/chat/:agentId", name: "CozeAgentChat", component: () => import("@/views/admin/CozeAgentChat.vue"), meta: { title: "智能体对话测试" } },
      { path: "range/package", redirect: "/admin/train/toolkit" },
      { path: "range/quiz", redirect: "/admin/index" },
      { path: "range/quiz/:setId", redirect: "/admin/index" },
      { path: "range/toolkit", redirect: "/admin/train/toolkit" },
    ],
  },
  {
    path: "/",
    component: () => import("@/layout/PortalLayout.vue"),
    redirect: "/index",
    meta: {},
    children: [
      {
        path: "index",
        name: "PortalHome",
        component: () => import("@/views/portal/Home.vue"),
        meta: { fullWidth: true, title: "首页" },
      },
      {
        path: "face/center",
        component: () => import("@/layout/PersonalCenterLayout.vue"),
        redirect: "/face/center/profile",
        meta: { crumbs: ["个人中心"], needAuth: true },
        children: [
          {
            path: "profile",
            name: "PersonalCenterProfile",
            component: () => import("@/views/portal/PersonalCenterProfile.vue"),
            meta: { crumbs: ["个人中心", "个人信息"] },
          },
          {
            path: "overview",
            name: "PersonalCenterOverview",
            component: () => import("@/views/portal/PersonalCenterOverview.vue"),
            meta: { crumbs: ["个人中心", "学习数据"] },
          },
          {
            path: "notes",
            name: "MyNotes",
            component: () => import("@/views/portal/MyNotes.vue"),
            meta: { crumbs: ["个人中心", "我的笔记"] },
          },
          {
            path: "courses",
            name: "MyCourse",
            component: () => import("@/views/portal/MyCourse.vue"),
            meta: { crumbs: ["个人中心", "我的课程"] },
          },
          {
            path: "password",
            name: "PersonalCenterPassword",
            component: () => import("@/views/portal/PersonalCenterPassword.vue"),
            meta: { crumbs: ["个人中心", "修改密码"] },
          },
          {
            path: "toolkit",
            name: "PersonalCenterToolkit",
            component: () => import("@/views/portal/ToolkitPage.vue"),
            meta: { crumbs: ["个人中心", "工具库"] },
          },
        ],
      },
      { path: "face/myCourse", redirect: "/face/center/courses" },
      { path: "face/mySchedule", name: "MySchedule", component: () => import("@/views/portal/MySchedule.vue"), meta: { needAuth: true } },
      {
        path: "face/bank",
        name: "MyBank",
        component: () => import("@/views/portal/QuestionBankList.vue"),
        meta: { crumbs: ["学习广场", "我的习题"] },
      },
      {
        path: "face/bank/ai-generate",
        name: "FaceAiExamGenerate",
        component: () => import("@/views/admin/AiExamGenerate.vue"),
        meta: { crumbs: ["学习广场", "AI 出卷"], title: "AI 出卷", needAuth: true },
      },
      { path: "face/homework", name: "HomeworkFace", component: () => import("@/views/portal/Homework.vue"), meta: { needAuth: true } },
      { path: "face/profile", name: "StudentProfile", component: () => import("@/views/portal/StudentProfile.vue"), meta: { crumbs: ["个人中心", "账号设置"], needAuth: true } },
      {
        path: "face/course",
        name: "AllCourse",
        component: () => import("@/views/portal/AllCourse.vue"),
        meta: { fullWidth: true, title: "课程分类" },
      },
      { path: "face/course/:courseId", name: "CourseIntro", component: () => import("@/views/portal/CourseIntro.vue") },
      {
        path: "face/course/:courseId/play",
        name: "CoursePlay",
        component: () => import("@/views/portal/CoursePlay.vue"),
        meta: { fullWidth: true, needAuth: true },
      },
      {
        path: "face/course/:courseId/practice",
        name: "PracticeLab",
        component: () => import("@/views/portal/PracticeLab.vue"),
        meta: { title: "实践实验室", practiceShell: true, hideGlobalBot: true, needAuth: true },
      },
      {
        path: "face/course/:courseId/practice/ide",
        name: "PracticeIdeWindow",
        component: () => import("@/views/portal/PracticeIdeWindow.vue"),
        meta: { title: "实践 IDE", practiceShell: true, hideGlobalBot: true, needAuth: true },
      },
      {
        path: "face/university",
        name: "UniversityZone",
        component: () => import("@/views/portal/UniversityZone.vue"),
        meta: { fullWidth: true, title: "高校专区" },
      },
      {
        path: "face/university/:slug",
        name: "UniversityDetail",
        component: () => import("@/views/portal/UniversityDetail.vue"),
        meta: { fullWidth: true, title: "高校详情" },
      },
      { path: "face/profession", name: "ProfessionFace", component: () => import("@/views/portal/Profession.vue") },
      { path: "face/race", redirect: "/face/learnPath" },
      {
        path: "face/learnPath",
        name: "LearnPath",
        component: () => import("@/views/portal/LearnPath.vue"),
        meta: { fullWidth: true, title: "学习路径" },
      },
      {
        path: "face/learnPath/:pathId",
        name: "LearnPathDetail",
        component: () => import("@/views/portal/LearnPathDetail.vue"),
        meta: { fullWidth: true, title: "学习路径详情" },
      },
      { path: "face/exam", name: "ExamFace", component: () => import("@/views/portal/Exam.vue"), meta: { needAuth: true } },
      { path: "face/online", redirect: "/index" },
      {
        path: "face/shoot",
        name: "ShootBank",
        component: () => import("@/views/portal/QuestionBankList.vue"),
        meta: { crumbs: ["靶场资源", "全部题库"] },
      },
      { path: "face/bank/:bankId", name: "BankQuizHub", component: () => import("@/views/portal/BankQuizHub.vue") },
      {
        path: "face/bank/:bankId/practice/:setId",
        name: "QuizTake",
        component: () => import("@/views/portal/QuizTake.vue"),
        meta: { hideGlobalBot: true, title: "在线答题", needAuth: true },
      },
      { path: "face/toolkit", redirect: "/face/center/toolkit" },
      { path: "face/workspace", name: "WorkspaceLearn", component: () => import("@/views/portal/WorkspaceLearn.vue"), meta: { needAuth: true } },
      { path: "face/myCenter", redirect: "/face/center" },
      { path: "face/allCourse", redirect: "/face/course" },
      { path: "face/path", redirect: "/face/learnPath" },
      { path: "face/question", redirect: "/face/bank" },
      { path: "face/range", redirect: "/face/toolkit" },
      { path: "face/quiz", redirect: "/face/shoot" },
      { path: "face/quiz/:setId", redirect: "/face/shoot" },
    ],
  },
];

const router = new VueRouter({
  // hash 模式：URL 带 #，如 /#/login；静态站无需 Nginx try_files，刷新不 404
  mode: "hash",
  routes,
});

function parseRoles(info) {
  const rolesRaw = info.data ? info.data.roles : info.roles;
  return (rolesRaw || []).map((r) => r.roleKey);
}

router.beforeEach(async (to, from, next) => {
  if (to.path === "/portal/login" || to.path === "/login") {
    if (isAdminDomain()) {
      next("/admin/login");
      return;
    }
    openPortalLogin(to.query.redirect || from.fullPath || from.path || "");
    next({ path: from.path && from.path !== to.path && from.path !== "/login" && from.path !== "/portal/login" ? from.path : "/index", replace: true });
    return;
  }
  if (to.meta.public) {
    if (isAdminDomain() && to.path !== "/admin/login") {
      next("/admin/login");
      return;
    }
  }
  if (isAdminDomain() && !to.path.startsWith("/admin") && !to.meta.public) {
    next("/admin/index");
    return;
  }
  if (isPortalDomain() && to.path.startsWith("/admin")) {
    next("/index");
    return;
  }
  if (to.meta.public) {
    next();
    return;
  }
  const needAuth = to.matched.some((m) => m.meta && m.meta.needAuth);
  const token = getToken();
  if (!token && needAuth) {
    if (to.path.startsWith("/admin") || isAdminDomain()) {
      next({ path: "/admin/login", query: { redirect: to.fullPath || to.path || "/" } });
      return;
    }
    handlePortalNeedAuth(to, from, next);
    return;
  }
  if (!token && !needAuth) {
    next();
    return;
  }
  if (!store.state.user) {
    try {
      const info = await getInfo();
      const u = info.data ? info.data.user : info.user;
      store.commit("SET_USER", u);
      store.commit("SET_ROLES", parseRoles(info));
    } catch (e) {
      store.commit("LOGOUT");
      if (to.path.startsWith("/admin") || isAdminDomain()) {
        next("/admin/login");
        return;
      }
      handlePortalNeedAuth(to, from, next);
      return;
    }
  }
  if (to.meta.admin) {
    const roles = store.state.roles || [];
    if (
      !roles.includes("admin") &&
      !roles.includes("manager") &&
      !roles.includes("super_admin") &&
      !roles.includes("platform_admin") &&
      !roles.includes("college_admin") &&
      !roles.includes("teacher")
    ) {
      next("/index");
      return;
    }
    const p = to.path || "";
    const isSuper = roles.includes("admin") || roles.includes("manager") || roles.includes("super_admin");
    const isPlatform = roles.includes("platform_admin");
    const isCollege = roles.includes("college_admin");
    if (p.startsWith("/admin/student/") && !isCollege) {
      next("/admin/index");
      return;
    }
    if (p.startsWith("/admin/user/account") && !isSuper) {
      next("/admin/index");
      return;
    }
    if (p.startsWith("/admin/teacher/") && !isCollege && !isSuper) {
      next("/admin/index");
      return;
    }
    if (p.startsWith("/admin/school/school") && !isSuper) {
      next("/admin/index");
      return;
    }
    if (p.startsWith("/admin/school/lesson-quota")) {
      const ut = Number(store.state.user && store.state.user.userType);
      if (!isSuper || ut !== 2) {
        next("/admin/index");
        return;
      }
    }
    if (p.startsWith("/admin/university/") && !isCollege) {
      next("/admin/index");
      return;
    }
    if (p.startsWith("/admin/system/dept") && (isSuper || isPlatform)) {
      next("/admin/index");
      return;
    }
    /** 平台管理员：与后端 school/university 等 403 策略一致，仅允许部分后台路径 */
    if (isPlatform) {
      const allowPlatform = [
        "/admin/index",
        "/admin/situation",
        "/admin/system/notice",
        "/admin/course/",
        "/admin/experiment/",
        "/admin/train/toolkit",
        "/admin/train/bank-manage",
        "/admin/train/bank-quiz",
        "/admin/train/bank-quiz-ai",
        "/admin/train/apps",
        "/admin/coze/",
      ];
      if (!allowPlatform.some((x) => p.startsWith(x))) {
        next("/admin/index");
        return;
      }
    }
    if (p.startsWith("/admin/train/cloudstudio")) {
      next("/admin/index");
      return;
    }
    if (
      p.startsWith("/admin/train/apps") &&
      !roles.includes("teacher") &&
      !isPlatform &&
      !isSuper &&
      !isCollege
    ) {
      next("/admin/index");
      return;
    }
    if (
      p.startsWith("/admin/scene/") ||
      p.startsWith("/admin/docker/") ||
      p.startsWith("/admin/operation/")
    ) {
      if (isSuper || isPlatform || roles.includes("teacher")) {
        next("/admin/index");
        return;
      }
    }
    if (
      p.startsWith("/admin/question/") ||
      p.startsWith("/admin/train/manage")
    ) {
      next("/admin/index");
      return;
    }
    if (isCollege) {
      const allowCollege = [
        "/admin/index",
        "/admin/system/dept",
        "/admin/system/notice",
        "/admin/student/student",
        "/admin/teacher/teacher",
        "/admin/course/course",
        "/admin/course/course-edit",
        "/admin/course/course-chapters",
        "/admin/course/course_type",
        "/admin/university/university",
        "/admin/school/lesson-manage",
        "/admin/train/apps",
        "/admin/train/toolkit",
        "/admin/train/bank-manage",
        "/admin/train/bank-quiz",
        "/admin/train/bank-quiz-ai",
      ];
      if (!allowCollege.some((x) => p.startsWith(x))) {
        next("/admin/index");
        return;
      }
    }
  }
  next();
});

export default router;
